import { FormikProps } from 'formik';
import { LpeFieldInterface, ObjStrKeyStrVal } from '@shared/types';
import {
  PhoneField as LpePhoneField,
  AccessibleTextField,
  AccessibleSelectField,
  AccessibleTextAreaField,
  AccessibleDependentSelectField,
} from '@shared/fields';

/**
 * Main Field component leveraging Formik Library.
 * Receives a single field and returns a Field component.
 */
const LpeField = ({
  field,
  requiredFields,
  dependentSelectOptions,
  selectOptions,
  selectFieldClassNames,
  textFieldClassNames,
  isTouchedAndError,
  formik,
}: LpeFieldInterface & { formik?: FormikProps<ObjStrKeyStrVal> }) => {
  const type = field.fieldType;

  const isRequired = requiredFields.includes(field.parameterName);
  const placeholder = isRequired ? `${field.name}*` : field.name;

  if (field.isHidden) {
    return null;
  }

  switch (type) {
    case 'PHONE':
      return (
        <LpePhoneField
          type={type}
          field={field}
          placeholder={placeholder}
          textFieldClassNames={textFieldClassNames}
          isTouchedAndError={isTouchedAndError}
          formik={formik as FormikProps<ObjStrKeyStrVal>}
          showErrorMessages={true}
        />
      );
    case 'SELECT':
      return (
        <AccessibleSelectField
          field={field}
          placeholder={placeholder}
          selectOptions={selectOptions}
          selectFieldClassNames={selectFieldClassNames}
          isTouchedAndError={isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={true}
        />
      );
    case 'CHECKBOX':
      // CHECKBOX is now handled separately in Formik.tsx with AccessibleSmsCheckbox
      return null;
    case 'DEPENDENT_SELECT':
      return (
        <AccessibleDependentSelectField
          field={field}
          placeholder={placeholder}
          dependentSelectOptions={dependentSelectOptions}
          selectFieldClassNames={selectFieldClassNames}
          isTouchedAndError={isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={true}
        />
      );
    case 'TEXTAREA':
      return (
        <AccessibleTextAreaField
          type={type}
          field={field}
          placeholder={placeholder}
          textFieldClassNames={textFieldClassNames}
          isTouchedAndError={isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={true}
        />
      );
    default:
      return (
        <AccessibleTextField
          type={type}
          field={field}
          placeholder={placeholder}
          textFieldClassNames={textFieldClassNames}
          isTouchedAndError={isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={true}
        />
      );
  }
};

export { LpeField };
